import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TabDropdownOption } from './ModuleNavigation';

export interface TabItem {
  label: string;
  path: string;
  disabled?: boolean;
  id?: string;
  isActive?: (currentPath: string) => boolean;
  /**
   * In-page tab: selecting it flips local state instead of navigating. When
   * set the tab renders as a button and `isActive` decides its state.
   */
  onSelect?: () => void;
  count?: number;
  badge?: 'beta' | 'coming-soon' | 'new';
  dropdownOptions?: TabDropdownOption[];
  hasDropdown?: boolean;
}

interface PageTabsProps {
  tabs: TabItem[];
  className?: string;
  /** Callback when a view is changed via dropdown (e.g., list/board/timeline) */
  onViewChange?: (tabId: string, viewId: string) => void;
}

export function PageTabs({ tabs, className, onViewChange }: PageTabsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  return (
    <div
      className={cn(
        "flex w-full min-w-0 max-w-full space-x-1 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-border sm:space-x-4",
        "overscroll-x-contain [-webkit-overflow-scrolling:touch]",
        className,
      )}
    >
      {tabs.map((tab) => {
        // Determine if tab is active using the custom function if provided
        // Otherwise fall back to simple path matching
        const isActive = tab.isActive
          ? tab.isActive(currentPath)
          : currentPath === tab.path || currentPath.endsWith(tab.path);

        // For tabs with dropdown options, check if any dropdown option is active
        const activeDropdownOption = tab.dropdownOptions?.find(option =>
          currentPath.endsWith(option.path) || currentPath.includes(`/${option.id}`)
        );
        const isDropdownActive = isActive || !!activeDropdownOption;

        // Get the current view label for dropdown tabs
        // If there's an active dropdown option (we're on that view), use its label
        // Otherwise, find the label that matches the tab's path (the saved preference)
        const savedViewOption = tab.dropdownOptions?.find(option => option.path === tab.path);
        const currentViewLabel = activeDropdownOption?.label || savedViewOption?.label || tab.dropdownOptions?.[0]?.label;

        // If this tab has dropdown options, render with an inline badge dropdown
        if (tab.hasDropdown && tab.dropdownOptions && tab.dropdownOptions.length > 0) {
          return (
            <div
              key={tab.id || tab.path}
              className={cn(
                "text-sm font-medium transition-colors whitespace-nowrap text-consistent shrink-0",
                "border-b-2 flex items-center",
                isDropdownActive
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30",
                tab.disabled && "opacity-50 cursor-not-allowed pointer-events-none",
              )}
            >
              <Link
                to={tab.disabled ? "#" : tab.path}
                className="px-2 sm:px-4 py-3 hover:text-foreground transition-colors flex items-center gap-2"
                onClick={(e) => {
                  if (tab.disabled) {
                    e.preventDefault();
                  }
                }}
              >
                {tab.label}
                {typeof tab.count === 'number' && tab.count > 0 && (
                  <Badge
                    variant="secondary"
                    className="h-5 px-1.5 text-xs font-normal bg-muted/60 text-muted-foreground border-0"
                  >
                    {tab.count.toLocaleString()}
                  </Badge>
                )}
              </Link>
              {/* View selector badge with dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      "h-5 px-1.5 mr-2 sm:mr-4 text-[10px] font-medium rounded-md flex items-center gap-0.5 transition-colors",
                      "bg-transparent text-muted-foreground border border-border/60 hover:bg-muted/30 hover:text-foreground hover:border-border"
                    )}
                  >
                    {currentViewLabel}
                    <CaretDownIcon className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[120px]">
                  {tab.dropdownOptions.map((option) => {
                    const isOptionActive = currentPath.endsWith(option.path) ||
                      currentPath.includes(`/${option.id}`);
                    return (
                      <DropdownMenuItem
                        key={option.id}
                        onClick={() => {
                          navigate(option.path);
                          // Notify parent of view change for preference persistence
                          if (onViewChange && tab.id) {
                            onViewChange(tab.id, option.id);
                          }
                        }}
                        className="cursor-pointer flex items-center justify-between"
                      >
                        <span>{option.label}</span>
                        {isOptionActive && (
                          <CheckIcon className="h-4 w-4 text-primary" />
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        }

        // Regular tab without dropdown. Tabs that carry an `onSelect`
        // switch in-page state (e.g. My Work's work-type lens) and render as
        // a button; the rest keep the Link + path behaviour.
        const tabClassName = cn(
          "px-2 sm:px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap text-consistent shrink-0",
          "border-b-2 flex items-center gap-2",
          isActive
            ? "border-primary text-foreground"
            : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30",
          tab.disabled && "opacity-50 cursor-not-allowed pointer-events-none",
        );

        const tabContent = (
          <>
            <span>{tab.label}</span>
            {tab.badge && (
              <Badge
                variant={tab.badge === 'beta' ? 'secondary' : tab.badge === 'new' ? 'success' : 'outline'}
                className={cn(
                  "h-5 px-1.5 text-[10px] font-medium uppercase tracking-wide",
                  tab.badge === 'beta' && "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
                  tab.badge === 'coming-soon' && "bg-muted text-muted-foreground",
                  tab.badge === 'new' && "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                )}
              >
                {tab.badge === 'coming-soon' ? 'Soon' : tab.badge}
              </Badge>
            )}
            {typeof tab.count === 'number' && tab.count > 0 && (
              <Badge
                variant="secondary"
                className="h-5 px-1.5 text-xs font-normal bg-muted/60 text-muted-foreground border-0"
              >
                {tab.count.toLocaleString()}
              </Badge>
            )}
          </>
        );

        if (tab.onSelect) {
          return (
            <button
              key={tab.id || tab.path}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={tabClassName}
              disabled={tab.disabled}
              onClick={tab.onSelect}
            >
              {tabContent}
            </button>
          );
        }

        return (
          <Link
            key={tab.path}
            to={tab.disabled ? "#" : tab.path}
            className={tabClassName}
            onClick={(e) => {
              if (tab.disabled) {
                e.preventDefault();
              }
            }}
          >
            {tabContent}
          </Link>
        );
      })}
    </div>
  );
}
